<template>
  <section class="section-base" id="scroll-target">
    <div class="section-base__title-box">
      <div class="section-base__title">
        {{ mapStatementName[statementType] }}
      </div>
    </div>

    <div class="alert-base" v-if="disclaimer">
      <v-alert prominent text color="primary">
        <span>
          Уважаемые пользователи!<br />
          {{ disclaimer }}
        </span>
      </v-alert>
    </div>

    <div
        class="alert-base"
        v-if="step === 10
        && statementType === StatementType.NOTICE_RECOVERY
        && originalStatus !== StatementStatus.REJECTED_FROM_FSSP
        && noticeRecoveryRegion"
    >
      <v-alert prominent text color="primary">
        <span>
          Уважаемые пользователи! <br>
          Обращаем ваше внимание, что в соответствии с требованиями законодательства заявление будет направленно нотариусам субъекта Российской Федерации, соответствующего месту жительства (регистрации) должника - физического лица или месту нахождения должника - юридического лица.
        </span>
      </v-alert>
    </div>

    <div class="alert-base" v-if="testAlertIsVisible">
      <v-alert
        icon="mdi-exclamation-thick"
        prominent
        text
        type="info"
        color="red"
      >
        В настоящее время проводятся технические работы. Сервис работает в
        тестовом режиме. Вы можете сохранить Заявление для дальнейшего
        редактирования и отправки.
      </v-alert>
    </div>

    <div class="alert-base" v-if="step === 1">
      <v-alert prominent text color="primary">
        <span v-if="roleType === Role.PERSON">
          Вы авторизовались как физическое лицо. Для подачи заявления от
          юридического лица авторизуйтесь как представитель юридического лица.
        </span>
        <span v-if="roleType === Role.ORGANIZATION">
          Вы авторизовались как юридическое лицо. Для подачи заявления от
          физического лица авторизуйтесь как физическое лицо.
        </span>
      </v-alert>
    </div>

    <v-stepper v-if="process" v-model="step" class="steppers-base">
      <v-stepper-header>
        <template v-for="n in stepSignN">
          <v-stepper-step
            :key="`step${n}`"
            :step="n"
            :complete="step > n || isStepLink(n)"
            class="step-item"
            :class="{ link: isStepLink(n), current: n === step }"
            @click="stepClickHandler(n)"
            >Шаг {{ n }}
          </v-stepper-step>
          <v-divider :key="`divider${n}`" v-if="n < stepSignN" />
        </template>
      </v-stepper-header>

      <SkeletonLoader v-if="!isShow" />
      <v-stepper-items>
        <v-stepper-content
          v-for="(stepComponent, index) in steps"
          :key="index"
          :step="index + 1"
        >
          <component
            :is="stepComponent"
            v-if="isShow && step === index + 1"
            :key="`step${index + 1}`"
            @next="
              step === steps.length
                ? onSaveStatementXml($event)
                : onNext($event)
            "
            @prev="onPrev"
            :loading-next="loadingNext"
            :statement-type="statementType"
            :role-type="roleType"
            :is-alternative-service-edit="isAlternativeServiceEdit"
            :is-surcharge="isSurcharge"
            :is-resend-to-fssp-statement="isResendToFsspStatement"
          />
        </v-stepper-content>
        <v-stepper-content :step="stepSignN">
          <StepSign
            v-if="isShow"
            :key="'stepSign'"
            @next="onNext"
            @prev="onPrev"
            @nextSign="onFinishStatement"
            :loading-next="
              loadingNext || alternativeServiceEdit.isNavigationDisabled
            "
            :statement-type="statementType"
            :is-alternative-service-edit="isAlternativeServiceEdit"
            :is-navigation-disabled="
              alternativeServiceEdit.isNavigationDisabled
            "
            :with-additional-signers="getWithAdditionalSigners()"
          >
            <component :is="stepSign" />
          </StepSign>
        </v-stepper-content>
        <v-stepper-content :step="steps.length + 2">
          <StepView
            v-if="isShow && step === steps.length + 2"
            :key="'stepView'"
            @prev="onPrev"
            :loading-next="loadingNext"
            :statement-id="statementId"
            :original-statement-id="originalStatementId"
            :is-alternative-service-edit="isAlternativeServiceEdit"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <StepSuccess v-if="success" :typeIcon="TypeIcon.SUCCESS" />

    <StepSuccess
      v-if="successNotSign"
      :typeIcon="TypeIcon.ALERT"
      description="Заявление сформировано и ожидает подписи всеми участниками."
    >
      <template v-slot:default>
        <v-btn
          depressed
          color="primary"
          class="button-base"
          @click="goToStatementsId"
          >Перейти к заявлению
        </v-btn>
      </template>
    </StepSuccess>

    <SignatureComponent />
  </section>
</template>

<script>
import { securingEvidenceSteps } from '@/components/services/SecuringEvidence/steps';

import AcceptanceEscrowStep1 from '@/components/services/AcceptanceEscrow/Step1';
import AcceptanceEscrowStep2 from '@/components/services/AcceptanceEscrow/Step2';
import AcceptanceEscrowStep3 from '@/components/services/AcceptanceEscrow/Step3';
import AcceptanceEscrowStep4 from '@/components/services/AcceptanceEscrow/Step4';
import AcceptanceEscrowStep5 from '@/components/services/AcceptanceEscrow/Step5';

import { storageAcceptSteps } from '@/components/services/StorageAccept/steps';

import RodSteps from '@/components/services/ROD/steps';
import RODStepSign from '@/components/services/ROD/StepSign';

import ExtractionRodSteps from '@/components/services/ExtractionRod/steps';

import StepDocuments from '@/components/services/common/StepDocuments';
import StepSign from '@/components/services/common/StepSign';
import StepSuccess, {
  TypeIcon,
} from '@/components/services/common/StepSuccess';
import StepView from '@/components/services/common/StepView';
import SkeletonLoader from '@/components/base/SkeletonLoader';
import { ServicesModule } from '@/store/modules/services';
import SignatureComponent from '@/components/base/SignatureComponent';
import { mapStatementName } from '@/utils/services';
import { AuthModule } from '@/store/modules/auth';
import { DisclaimerModule } from '@/store/modules/disclaimer';
import { Role } from '@/utils/dictionaries';
import { StatementType } from '@/types/services';
import {noticeRecoveryRegion, statementTypeToIsTestMode} from '@/app.config';
import { RouteName } from '@/router';
import { EditedStatementModule } from '@/store/modules/alternativeServices/editedStatement';
import { StatementStatusInfoModule } from '@/store/modules/statementStatusInfo';
import { AdditionalDocumentsType } from '@/types/alternativeServices';
import { egrnConfirmSteps } from '@/components/services/EgrnConfirm/steps';
import { noticeRecoverySteps } from '@/components/services/NoticeRecovery/steps';
import { acceptanceDepositSteps } from '@/components/services/AcceptanceDeposit/steps';
import { storageIssueSteps } from '@/components/services/StorageIssue/steps';
import { correctTranslationSteps } from '@/components/services/CorrectTranslation/steps';
import { transferElDocsSteps } from '@/components/services/TransferElDocs/steps';
import { ruzdiSteps } from '@/components/services/RUZDI/steps';
import { documentConversionSteps } from '@/components/services/DocumentConversion/steps';
import { statusInfoService } from '@/services';
import { errorHandler } from '@/utils/common';
import {StatementStatus} from "@/constants";
import {loadPageMessages} from '@/store/i18n';

const ServiceSteps = {
  [StatementType.NOTICE_RECOVERY]: noticeRecoverySteps,
  [StatementType.RUZDI]: ruzdiSteps,
  [StatementType.SECURING_EVIDENCE]: securingEvidenceSteps,
  [StatementType.ACCEPTANCE_DEPOSIT]: acceptanceDepositSteps,
  [StatementType.TRANSFER_EL_DOCS]: transferElDocsSteps,
  [StatementType.CORRECT_TRANSLATION]: correctTranslationSteps,
  [StatementType.ACCEPTANCE_ESCROW]: [
    AcceptanceEscrowStep1,
    AcceptanceEscrowStep2,
    AcceptanceEscrowStep3,
    AcceptanceEscrowStep4,
    AcceptanceEscrowStep5,
    StepDocuments,
  ],
  [StatementType.STORAGE_ACCEPT]: storageAcceptSteps,
  [StatementType.STORAGE_ISSUE]: storageIssueSteps,
  [StatementType.DOCUMENT_CONVERSION]: documentConversionSteps,
  [StatementType.ROD]: [...RodSteps, StepDocuments],
  [StatementType.EXTRACTION_ROD]: [...ExtractionRodSteps, StepDocuments],
  [StatementType.EGRN_CONFIRM]: [...egrnConfirmSteps, StepDocuments],
};

export default {
  name: 'ServiceBody',
  components: {
    StepSuccess,
    StepView,
    StepSign,
    SkeletonLoader,
    SignatureComponent,
  },
  props: {
    statementId: {
      type: String,
    },
    originalStatementId: {
      type: String,
    },
    statementType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      mapStatementName,
      StatementStatus,
      step: 1,
      process: true,
      success: false,
      successNotSign: false,
      isShow: false,
      loadingNext: false,
      alternativeServiceEdit: {
        isNavigationDisabled: false,
        archiveFormedInterval: null,
      },
      Role,
      TypeIcon,
      StatementType,
    };
  },
  computed: {
    steps() {
      return ServiceSteps[this.statementType];
    },
    originalStatus () {
      return StatementStatusInfoModule.entity?.statusCode || ''
    },

    stepSign() {
      switch (this.statementType) {
        case StatementType.ROD:
          return RODStepSign;
        default:
          return null;
      }
    },

    stepCount() {
      return this.steps.length + 2;
    },

    roleType() {
      return AuthModule.roleType;
    },

    testAlertIsVisible() {
      const isTestMode = statementTypeToIsTestMode[this.statementType];
      const isStep = this.step === 1 || this.step === this.stepSignN;

      return isTestMode && isStep;
    },

    stepSignN() {
      return this.steps.length + 1;
    },

    disclaimer() {
      return DisclaimerModule.text;
    },

    isAlternativeServiceEdit() {
      return this.$route.name.startsWith(RouteName.ALTERNATIVE_SERVICE_EDIT);
    },

    isSurcharge() {
      return Boolean(ServicesModule.statement.parentSurchargeId);
    },

    // ALTERNATIVE_SERVICE_EDIT
    isArchiveFormed() {
      return EditedStatementModule.isArchiveFormed.entity;
    },

    // ALTERNATIVE_SERVICE_EDIT
    isArchiveStartedFormed() {
      return (
        this.isAlternativeServiceEdit && ServicesModule.statement.zipFileLink
      );
    },

    isResendToFsspStatement() {
      return Boolean(
        StatementStatusInfoModule.entity?.additionalStatements.some(
          (addStatement) =>
            addStatement.type === AdditionalDocumentsType.RESEND_TO_FSSP &&
            addStatement.status === 'DRAFT'
        )
      );
    },

    applicantRightholder() {
      return ServicesModule.statement?.subjects?.applicantRightholder;
    },
    noticeRecoveryRegion() {
      return noticeRecoveryRegion
    },
  },
  watch: {
    // ALTERNATIVE_SERVICE_EDIT
    async isArchiveFormed(formed) {
      if (formed) {
        await StatementStatusInfoModule.get(this.originalStatementId);
        const originalStatusInfo = StatementStatusInfoModule.entity;

        if (statusInfoService.canAddDocuments(originalStatusInfo)) {
          const addStatement = statusInfoService.getAddDocumentsDraft(
            originalStatusInfo.additionalStatements
          );

          await this.$router.push({
            name: RouteName.ADD_DOCUMENTS_SIGN,
            params: {
              altStatementId: addStatement.id,
            },
          });

          return;
        }

        if (statusInfoService.canResendToFssp(originalStatusInfo)) {
          const addStatement = statusInfoService.getResendToFsspDraft(
            originalStatusInfo.additionalStatements
          );

          await this.$router.push({
            name: RouteName.RESEND_TO_FSSP_SIGN,
            params: { altStatementId: addStatement.id },
          });

          return;
        }

        errorHandler('Ошибка: нет доп заявления');
        await this.scrollToTop();
      }
    },
  },
  async mounted() {
    await this.getData();
    await DisclaimerModule.get(this.statementType);

    // ALTERNATIVE_SERVICE_EDIT
    if (this.isAlternativeServiceEdit && this.isArchiveStartedFormed) {
      this.step = this.stepSignN;
      this.alternativeServiceEdit.isNavigationDisabled = true;
      this.startCheckIsArchiveFormed();
    }

    // check RESEND_TO_FSSP
    if (this.isAlternativeServiceEdit) {
      await StatementStatusInfoModule.get(this.originalStatementId);
    }
    
  },
  created() {
    if (this.statementType === StatementType.NOTICE_RECOVERY) {
      loadPageMessages('noticeRecovery');
    }
  },
  beforeDestroy() {
    this.endCheckIsArchiveFormed();
  },
  methods: {
    stepClickHandler(n) {
      if (this.alternativeServiceEdit.isNavigationDisabled) return;

      this.goToStep(n);
    },

    async getData() {
      const id = this.isAlternativeServiceEdit
        ? this.originalStatementId
        : this.statementId;

      // если есть id - загружаем заявление с бека
      if (id) {
        this.isShow = await ServicesModule.getStatementById({
          id,
          isAlternativeServiceEdit: this.isAlternativeServiceEdit,
        });
      } else {
        await ServicesModule.createModel(this.statementType);
        this.isShow = true;
      }
    },

    onNext(data) {
      if (this.step < this.stepCount) this.saveStatement(data, true);
    },

    onPrev(data) {
      if (this.step <= this.stepCount && this.step > 1) {
        this.saveStatement(data, false);
      }
    },

    isStepLink(number) {
      return (
        ServicesModule.statement.stepsCompleted &&
        ServicesModule.statement.stepsCompleted.includes('step' + number)
      );
    },

    goToStep(number) {
      if (this.isStepLink(number)) {
        this.step = number;
      }
    },

    async saveStatement(data, isNext) {
      this.loadingNext = true;

      if (this.step < this.stepCount - 1) {
        ServicesModule.addStepsCompleted(`step${this.step}`);
      }

      ServicesModule.MAP_STATEMENT({ data, statementType: this.statementType });

      const dataSaved = await ServicesModule.saveStatement(
        this.isAlternativeServiceEdit
      );

      if (dataSaved) {
        this.step = isNext ? this.step + 1 : this.step - 1;
      }

      await this.scrollToTop();

      this.loadingNext = false;
    },

    async onSaveStatementXml(data) {
      this.loadingNext = true;

      ServicesModule.addStepsCompleted(`step${this.step}`);
      ServicesModule.MAP_STATEMENT({ data, statementType: this.statementType });

      const dataSave = await ServicesModule.saveStatement(
        this.isAlternativeServiceEdit
      );
      const dataXml = dataSave
        ? await ServicesModule.createStatementXml(this.isAlternativeServiceEdit)
        : undefined;

      await this.scrollToTop();

      if (dataXml) this.step++;

      this.loadingNext = false;
    },

    async onFinishStatement() {
      if (this.getWithAdditionalSigners()) {
        await this.onSendNotSignStatement();
      } else {
        await this.onSignStatement();
      }
    },

    // через computed не работает
    getWithAdditionalSigners() {
      if (this.statementType === StatementType.EGRN_CONFIRM) {
        return Boolean(ServicesModule.statement.subjects.applicantRightholder);
      }

      return [StatementType.ACCEPTANCE_ESCROW].includes(this.statementType);
    },

    async onSignStatement() {
      this.loadingNext = true;

      // Подписание XML и отправка пакета на бэкенд
      try {
        // получаем XmlBase64 для подписи
        const dataBase64 = await ServicesModule.statementXmlBase64(
          this.isAlternativeServiceEdit
        );
        const runSignProps = [{ xmlSigBase64: dataBase64 }];

        if (this.statementType !== StatementType.ACCEPTANCE_ESCROW) {
          runSignProps.push(ServicesModule.dataUserCertificate);
        }

        // подписываем XmlBase64 и получаем подпись в Base64
        const signs = await ServicesModule.runSign(...runSignProps);

        ServicesModule.setStatementXmlSigBase64(signs.xmlSigBase64[0]);

        if (this.isAlternativeServiceEdit) {
          await ServicesModule.editedFormPackage();
          this.alternativeServiceEdit.isNavigationDisabled = true;
          this.startCheckIsArchiveFormed();
        } else {
          await ServicesModule.sendPackage();
          await this.scrollToTop();
          this.process = false;
          this.success = true;
        }
      } catch {
        await this.scrollToTop();
      }

      this.loadingNext = false;
    },

    async scrollToTop() {
      await this.$vuetify.goTo('#header-target', { duration: 200 });
    },

    startCheckIsArchiveFormed() {
      EditedStatementModule.checkIsArchiveFormed(this.originalStatementId);

      this.alternativeServiceEdit.archiveFormedInterval = setInterval(() => {
        EditedStatementModule.checkIsArchiveFormed(this.originalStatementId);
      }, 3000);
    },

    endCheckIsArchiveFormed() {
      clearInterval(this.alternativeServiceEdit.archiveFormedInterval);
    },

    async onSendNotSignStatement() {
      this.loadingNext = true;

      try {
        await ServicesModule.notifyAdditionalSigners();
        await this.scrollToTop();
        this.process = false;
        this.successNotSign = true;
      } catch {
        await this.scrollToTop();
      }

      this.loadingNext = false;
    },

    goToStatementsId() {
      this.$router.push({
        name: RouteName.STATEMENT_DETAIL,
        params: { id: this.statementId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.steppers-base {
  & .step-item {
    &.link {
      &:hover {
        cursor: pointer;

        &::v-deep {
          .v-stepper__step__step {
            background-color: $color-base !important;
            border-color: $color-base !important;
          }
        }
      }
    }

    &.current {
      &::v-deep {
        .v-stepper__step__step {
          background-color: $color-base !important;
          border-color: $color-base !important;
        }
      }
    }
  }
}
</style>
